import React from 'react'
import { DumbField } from '../Wizard2/Field'
import { TextInput } from '../Wizard2/fieldComponents/Form'
import { AddressBlock as AddressClass } from '../Wizard2/WizardClasses'
import AddressBlock from '../Wizard2/fieldComponents/AddressBlock'
import { MASK, VALIDATE } from '../Wizard2/validate'
import { PaymentButton } from './Checkout'
import VisaMastercard from '../../common/img/credit-card-icons/visa_mastercard.gif'

const ALLOW_NATIVE_CHECKOUT = process.env.ALLOW_NATIVE_CHECKOUT
const PAYMENT_ALLOW_AMEX = process.env.PAYMENT_ALLOW_AMEX

const initialValues = {
  FIRST_NAME: '',
  LAST_NAME: '',
  COMPANY: '',
  PHONE_NUMBER: '',
  PHONE_EXTENSION: '',
  CC_NUMBER: '',
  CC_EXPIRATION: '',
  CC_CVV: '',
  BILLING_ADDRESS: new AddressClass({}).value,
}

export default ({
  children,
  active,
  disabled,
  error,
  processing,
  onDeactivateRequested,
  onSubmit,
}) => {
  const [values, dispatchValues] = React.useReducer((state, action) => {
    switch (action.type) {
      case 'SET_VALUE':
        return { ...state, [action.name]: action.value }
      default:
        return state
    }
  }, initialValues)
  const [errors, setErrors] = React.useState({})
  const wrapperRef = React.useRef()
  const firstNameRef = React.useRef()
  const shouldFocusFirst = React.useRef(false)

  const setValue = (value, e) =>
    dispatchValues({ type: 'SET_VALUE', name: typeof e === 'object' ? e.target.name : e, value })

  const validateAndSubmit = () => {
    getEntryIsValid(values).then((allValid) => {
      if (allValid) {
        onSubmit(values)
      }
    })
  }

  const getEntryIsValid = (values) =>
    new Promise((resolve, reject) => {
      setErrors({})
      let errors = {}

      const setError = (name, message) => {
        if (!errors[name]) {
          errors[name] = [message]
        }
      }

      ;['FIRST_NAME', 'LAST_NAME', 'PHONE_NUMBER', 'CC_NUMBER', 'CC_EXPIRATION', 'CC_CVV'].forEach(
        (v) => {
          if (!values[v]) setError(v, 'This field is required.')
        }
      )

      if (values.CC_NUMBER.length < 13) {
        setError('CC_NUMBER', 'Credit card number must be at least 13 numbers.')
      }

      let month = parseInt(values.CC_EXPIRATION.slice(0, 2), 10)
      let year = parseInt(values.CC_EXPIRATION.slice(2, 4), 10)
      if (values.CC_EXPIRATION.length !== 4 && month <= 12 && month > 0 && year > 0) {
        setError('CC_EXPIRATION', 'Please provide an expiration in the format MM/YY')
      }

      let promises = [
        new Promise((res, rej) =>
          VALIDATE.addressIsRequired(values.BILLING_ADDRESS, {}, {}, (isValid, errorMessage) => {
            res({
              isValid,
              errorMessage,
              name: 'BILLING_ADDRESS',
            })
          })
        ),
        new Promise((res, rej) =>
          VALIDATE.validPhoneNumber(
            values.PHONE_NUMBER,
            {},
            { errorMessage: 'Enter a valid phone number.' },
            (isValid, errorMessage) => {
              res({
                isValid,
                errorMessage,
                name: 'PHONE_NUMBER',
              })
            }
          )
        ),
      ]

      Promise.all(promises).then((results) => {
        results.forEach((result) => {
          if (!result.isValid) {
            setError(result.name, result.errorMessage)
          }
        })
        setErrors(errors)
        resolve(Object.keys(errors).length === 0)
      })
    })

  const getMaskedExpiration = (value) =>
    value.slice(0, 4).replace(/(?:([0-9]{2})([0-9{1}])|([0-9]{2})([0-9]{2}))/, '$1/$2')

  const calculatedDisabled = !active || disabled

  React.useEffect(() => {
    if (error) {
      document.getElementById('sliding-panel-scrollable').scrollTop =
        wrapperRef.current.offsetHeight
    }
  }, [error])

  React.useEffect(() => {
    if (active) {
      shouldFocusFirst.current = true
    } else {
      shouldFocusFirst.current = false
    }
  }, [active])

  const focusFirstField = () => {
    if (shouldFocusFirst.current) {
      firstNameRef.current.focus()
      shouldFocusFirst.current = false
    }
  }

  return (
    <div className="checkout-body native-checkout">
      <div
        className="checkout-body-inner-wrapper"
        style={{ transform: `translateX(${active ? -50 : 0}%)` }}
        ref={wrapperRef}
        onTransitionEnd={focusFirstField}
      >
        <fieldset className="main-wrapper" disabled={active} aria-hidden={active}>
          {children}
        </fieldset>

        {ALLOW_NATIVE_CHECKOUT && (
          <fieldset
            className="secondary-wrapper wizard"
            disabled={calculatedDisabled}
            aria-hidden={!active}
          >
            <div style={{ marginBottom: 10 }}>
              <button
                onClick={onDeactivateRequested}
                disabled={calculatedDisabled}
                className="return-button"
              >
                {'<'} Choose a different payment method
              </button>
            </div>
            <div className="flex-row">
              <DumbField
                fieldId="co-first-name"
                label="First Name"
                required
                errorMessage={errors.FIRST_NAME}
              >
                <TextInput
                  fieldId="co-first-name"
                  value={values.FIRST_NAME}
                  name="FIRST_NAME"
                  onChange={setValue}
                  ariaLabel="First Name"
                  disabled={calculatedDisabled}
                  ref={firstNameRef}
                />
              </DumbField>
              <DumbField
                fieldId="co-last-name"
                label="Last Name"
                required
                errorMessage={errors.LAST_NAME}
              >
                <TextInput
                  fieldId="co-last-name"
                  value={values.LAST_NAME}
                  name="LAST_NAME"
                  onChange={setValue}
                  ariaLabel="Last Name"
                  disabled={calculatedDisabled}
                />
              </DumbField>
            </div>
            <div className="flex-row">
              <DumbField fieldId="co-company" label="Company">
                <TextInput
                  fieldId="co-company"
                  value={values.COMPANY}
                  name="COMPANY"
                  onChange={setValue}
                  ariaLabel="Company"
                  width="100%"
                  disabled={calculatedDisabled}
                />
              </DumbField>
            </div>
            <div className="flex-row">
              <DumbField
                fieldId="co-cc-number"
                label="Credit Card Number"
                required
                errorMessage={errors.CC_NUMBER}
              >
                <TextInput
                  fieldId="co-cc-number"
                  value={values.CC_NUMBER}
                  name="CC_NUMBER"
                  onChange={(value, e) => setValue(MASK.onlyNumbers(value), e.target.name)}
                  maxLength={16}
                  ariaLabel="Credit Card Number"
                  width="100%"
                  disabled={calculatedDisabled}
                />
              </DumbField>
            </div>
            <div className="flex-row exp-row">
              <DumbField fieldId="co-cc-exp" label="Expiration" required>
                <TextInput
                  fieldId="co-cc-exp"
                  value={getMaskedExpiration(values.CC_EXPIRATION)}
                  name="CC_EXPIRATION"
                  placeholder="MM/YY"
                  onChange={(value, e) =>
                    setValue(MASK.onlyNumbers(value).slice(0, 4), e.target.name)
                  }
                  ariaLabel="Credit Card Expiration Date"
                  disabled={calculatedDisabled}
                  disableSelectionRestoration={true}
                />
              </DumbField>
              <DumbField fieldId="co-cc-cvv" label="CVV" required>
                <TextInput
                  fieldId="co-cc-cvv"
                  value={values.CC_CVV}
                  name="CC_CVV"
                  onChange={(value, e) => setValue(MASK.onlyNumbers(value), e.target.name)}
                  ariaLabel="Credit Card CVV"
                  maxLength={PAYMENT_ALLOW_AMEX ? 4 : 3}
                  disabled={calculatedDisabled}
                />
              </DumbField>
            </div>
            <div className="flex-column">
              <div>
                <p
                  className="field-error"
                  aria-live="polite"
                  style={{ display: !errors.CC_EXPIRATION ? 'none' : '' }}
                >
                  {errors.CC_EXPIRATION}
                </p>
              </div>
              <div>
                <p
                  className="field-error"
                  aria-live="polite"
                  style={{ display: !errors.CC_CVV ? 'none' : '' }}
                >
                  {errors.CC_CVV}
                </p>
              </div>
            </div>
            <div className="separator" style={{ marginTop: 10, marginBottom: 0 }} />
            <div className="flex-row" style={{marginTop: 15}}>
              <div>
                <img className="card-icons" alt="Visa & Mastercard accepted" src={VisaMastercard} />
              </div>
              <p className="credit-card-description">We accept Visa and Mastercard</p>
            </div>
            <div className="separator" style={{ marginTop: 10, marginBottom: -5 }} />
            <DumbField errorMessage={errors.BILLING_ADDRESS}>
              <AddressBlock
                required
                value={values.BILLING_ADDRESS}
                name="BILLING_ADDRESS"
                onChange={(value) => setValue(value, 'BILLING_ADDRESS')}
                allowSelection={false}
                showCityStateZip
                singleColSize="col-12"
                triColSizes={{ CITY: 'col-12 no-padding', STATE: 'col-4', ZIP: 'col-8' }}
                address1Label="Billing Address"
                disabled={calculatedDisabled}
                showAddress3={false}
                classNames={{
                  STATE: 'state-select',
                }}
              />
            </DumbField>
            <div className="flex-row phone-row">
              <DumbField
                fieldId="co-phone"
                label="Phone Number"
                required
                errorMessage={errors.PHONE_NUMBER}
              >
                <TextInput
                  fieldId="co-phone"
                  value={MASK.phone(values.PHONE_NUMBER)}
                  name="PHONE_NUMBER"
                  onChange={(value, e) => setValue(MASK.phone(value), e.target.name)}
                  ariaLabel="Phone Number"
                  maxLength={30}
                  width="100%"
                  disabled={calculatedDisabled}
                  disableSelectionRestoration
                />
              </DumbField>
              <DumbField fieldId="co-extension" label="Extension">
                <TextInput
                  fieldId="co-extension"
                  value={values.PHONE_EXTENSION}
                  name="PHONE_EXTENSION"
                  onChange={(value, e) => setValue(MASK.onlyNumbers(value), e.target.name)}
                  ariaLabel="Phone Extension"
                  maxLength={10}
                  autocomplete="tel-extension"
                  width="100%"
                  disabled={calculatedDisabled}
                />
              </DumbField>
            </div>
            <div style={{ marginTop: 15 }}>
              <PaymentButton
                bypassLoadingStyles
                disabled={calculatedDisabled}
                loading={processing}
                onClick={validateAndSubmit}
              >
                Submit Payment
              </PaymentButton>
            </div>
            <p
              className="method-error native-method-error"
              style={{ display: !error ? 'none' : '', marginTop: 15 }}
              aria-live="assertive"
            >
              {error}
            </p>
            {/* <CreditCard /> */}
          </fieldset>
        )}
      </div>
    </div>
  )
}

// const CreditCard = () => (
//   <div className="credit-card">
//     <div className="flex-row">
//
//     </div>
//   </div>
// )



// WEBPACK FOOTER //
// ./src/components/Checkout/NativeCheckout.js